import React, { useEffect, useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  InputAdornment,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faTrash, faSave } from '@fortawesome/free-solid-svg-icons';
import { createRouletteOption, deleteRouletteOption, getRouletteOptions, updateRouletteOption } from '../../../../api/roulette';
import { IRouletteOption } from '../../../../types/roulette';
import axios from 'axios';
import { updateRouletteStatus } from '../../../../api/general-info';

const RoulettePage: React.FC = () => {
  const [rouletteOptions, setRouletteOptions] = useState<IRouletteOption[]>([]);
  const [isRouletteActive, setIsRouletteActive] = useState<boolean>(false);
  const [rouletteMinPrice, setRouletteMinPrice] = useState<number>(0);
  const [originalMinPrice, setOriginalMinPrice] = useState<number>(0);
  
  useEffect(() => {
    const fetchRouletteOptions = async () => {
      try {
        const response = await getRouletteOptions();

        setRouletteOptions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching roulette options:', error);
        setRouletteOptions([]);
      }
    };

    const fetchGeneralInfo = async () => {
      try {
        const info = JSON.parse(localStorage.getItem('info') || '{}'); 

        if (info) {
          if (info.isRouletteActive !== undefined) {
            setIsRouletteActive(info.isRouletteActive);
          }

          if (info.rouletteMinPrice !== undefined) {
            setRouletteMinPrice(info.rouletteMinPrice);
            setOriginalMinPrice(info.rouletteMinPrice);
          }
        }
      } catch (error) {
        console.error('Error fetching general info:', error);
      }
    };

    fetchRouletteOptions();
    fetchGeneralInfo();
  }, []);

  const addRouletteOption = () => {
    const newOption: IRouletteOption = {
      option: '',
      style: { backgroundColor: '#ffffff', textColor: '#000000' },
      weight: 0,
    };

    setRouletteOptions([...rouletteOptions, newOption]);
  };
  
  const removeRouletteOption = async (index: number) => {
    const option = rouletteOptions[index];

    if (option?._id) {
      try {
        await deleteRouletteOption(option._id);
      } catch (error) {
        console.error('Error deleting roulette option:', error);

        return;
      }
    }

    const updatedOptions = rouletteOptions.filter((_, i) => i !== index);

    setRouletteOptions(updatedOptions);
  };
  
  const handleRouletteChange = (
    index: number,
    field: string,
    value: string | number
  ) => {
    const updatedOptions = [...rouletteOptions];
    const option = updatedOptions[index];

    if (!option) {return;}

    if (field.startsWith('style.')) {
      const styleField = field.split('.')[1] as keyof IRouletteOption['style'];

      updatedOptions[index] = {
        ...option,
        style: {
          ...option.style,
          [styleField]: value,
        },
      };
    } else {
      updatedOptions[index] = {
        ...option,
        [field as keyof IRouletteOption]: value,
      };
    }

    setRouletteOptions(updatedOptions);
  };
  
  const handleWeightChange = (index: number, value: number) => {
    const updatedOptions = [...rouletteOptions];
    const option = updatedOptions[index];

    if (!option) {return;}

    const newWeight = isNaN(value) ? 0 : value;

    updatedOptions[index].weight = newWeight;
    setRouletteOptions(updatedOptions);
  };
  
  const handleRouletteToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;

    setIsRouletteActive(newValue);
    
    try {
      await updateRouletteStatus(newValue, rouletteMinPrice);
      
      // Update localStorage if it's used in your app
      const info = localStorage.getItem('info');

      if (info) {
        const parsedInfo = JSON.parse(info);

        parsedInfo.isRouletteActive = newValue;
        parsedInfo.rouletteMinPrice = rouletteMinPrice;
        localStorage.setItem('info', JSON.stringify(parsedInfo));
      }
      
      console.log(`Roulette feature ${newValue ? 'activated' : 'deactivated'} successfully`);
      setOriginalMinPrice(rouletteMinPrice);
    } catch (error) {
      console.error('Error updating roulette status:', error);
      // Revert state if the API call fails
      setIsRouletteActive(!newValue);
    }
  };

  const handleMinPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);

    setRouletteMinPrice(isNaN(value) ? 0 : value);
  };

  const saveRouletteMinPrice = async () => {
    try {
      await updateRouletteStatus(isRouletteActive, rouletteMinPrice);
      
      // Update localStorage if it's used in your app
      const info = localStorage.getItem('info');

      if (info) {
        const parsedInfo = JSON.parse(info);

        parsedInfo.rouletteMinPrice = rouletteMinPrice;
        localStorage.setItem('info', JSON.stringify(parsedInfo));
      }
      
      setOriginalMinPrice(rouletteMinPrice);
    } catch (error) {
      console.error('Error updating minimum price:', error);
      setRouletteMinPrice(originalMinPrice);
    }
  };
  
  const handleSave = async () => {
    try {
      for (const option of rouletteOptions) {
        const payload = {
          option: option.option || '',
          style: option.style || { backgroundColor: '#ffffff', textColor: '#000000' },
          weight: option.weight || 0,
        };

        if (option._id) {
          await updateRouletteOption(payload, option._id);
        } else {
          const response = await createRouletteOption(payload);

          option._id = response.data._id; 
        }
      }

      setRouletteOptions([...rouletteOptions]); 
      console.log('All changes saved successfully');
    } catch (error) {
      console.error('Error saving roulette options:', error);
    }
  };
  
  const calculateTotalWeight = () => {
    return rouletteOptions.reduce((sum, option) => sum + (option?.weight || 0), 0);
  };

  return (
    <Box sx={{ m: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Setări Ruletă
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={isRouletteActive}
                  onChange={handleRouletteToggle}
                  color="primary"
                />
              }
              label={isRouletteActive ? "Activă" : "Inactivă"}
            />
          </Grid>
          
          <Grid item xs={12} sm={5}>
            <TextField
              label="Preț minim pentru activare (lei)"
              type="number"
              value={rouletteMinPrice}
              onChange={handleMinPriceChange}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">Lei</InputAdornment>,
                inputProps: { min: 0, step: 1 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Button 
              variant="contained" 
              size="small"
              startIcon={<FontAwesomeIcon icon={faSave} />}
              onClick={saveRouletteMinPrice}
              disabled={rouletteMinPrice === originalMinPrice}
            >
              Salvează preț
            </Button>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {isRouletteActive 
                ? `Clienții vor vedea roata când plasează comenzi de minim ${rouletteMinPrice} Lei și pot câștiga premii.`
                : "Ruleta este dezactivată. Clienții vor plasa comenzi fără a vedea roata."}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Opțiuni Ruletă
      </Typography>

      {/* Afișare greutate totală și erori */}
      <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#f8f8f8' }}>
        <Typography variant="subtitle2">
          Greutate Totală: {calculateTotalWeight().toFixed(1)}%
        </Typography>
        {calculateTotalWeight() > 100 && (
          <Typography variant="body2" color="error">
            Eroare: Greutatea totală depășește 100%
          </Typography>
        )}
      </Box>

      {rouletteOptions.map((option, index) => (
        <Box
          key={option?._id || `new-${index}`}
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid #ccc',
            borderRadius: 2,
            borderLeftWidth: '5px',
            borderLeftColor: option?.style?.backgroundColor || '#ffffff',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Nume Opțiune"
                value={option?.option || ''}
                onChange={(e) => handleRouletteChange(index, 'option', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography variant="body2" gutterBottom>
                Culoare Fundal
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: '36px',
                    height: '36px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    mr: 1,
                    bgcolor: option?.style?.backgroundColor || '#ffffff',
                  }}
                />
                <input
                  type="color"
                  value={option?.style?.backgroundColor || '#ffffff'}
                  onChange={(e) =>
                    handleRouletteChange(index, 'style.backgroundColor', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography variant="body2" gutterBottom>
                Culoare Text
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: '36px',
                    height: '36px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    mr: 1,
                    bgcolor: option?.style?.textColor || '#000000',
                  }}
                />
                <input
                  type="color"
                  value={option?.style?.textColor || '#000000'}
                  onChange={(e) =>
                    handleRouletteChange(index, 'style.textColor', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={1.5}>
              <TextField
                label="Şansa (%)"
                type="number"
                value={option?.weight || 0}
                onChange={(e) => handleWeightChange(index, parseFloat(e.target.value))}
                fullWidth
                variant="outlined"
                size="small"
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>

            <Grid item xs={12} sm={0.5}>
              <IconButton
                onClick={() => removeRouletteOption(index)}
                color="error"
                disabled={!option?._id}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      ))}

      <Button
        variant="outlined"
        startIcon={<FontAwesomeIcon icon={faAdd} />}
        onClick={addRouletteOption}
        sx={{ mt: 2, mr: 2 }}
      >
        Adaugă Opțiune
      </Button>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        sx={{ mt: 2 }}
      >
        Salvează Tot
      </Button>
    </Box>
  );
};

export default RoulettePage;